import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CheckCircle, CalendarPlus, CalendarClock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  ScheduleMessagesForm,
  buildSchedulePayload,
  emptyScheduleMessage,
  type DraftScheduleMessage,
} from "@/components/conversations/ScheduleMessagesForm";
import { ScheduleActionOption } from "@/components/conversations/ScheduleActionOption";

interface ScheduleCloseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conversationId: string;
  leadName: string;
  workspaceId: string;
  onJustClose: () => void;
  onJustClosePreserveAssignees?: () => void;
  onScheduledClose: (didCloseConversation: boolean) => void;
}

type ScheduleMode = "choose" | "schedule_close" | "schedule_open";

const SCHEDULE_ERROR_MAP: Record<string, string> = {
  conversation_not_found: "Conversa não encontrada.",
  workspace_mismatch: "Sem permissão para esta conversa.",
  scheduled_messages_whatsapp_only: "Agendamento disponível apenas para WhatsApp.",
  conversation_has_no_lead: "Conversa sem lead associado.",
  no_active_whatsapp_integration: "Nenhuma integração WhatsApp ativa.",
  too_many_messages: "Máximo de 5 mensagens por agendamento.",
  no_messages_provided: "Adicione ao menos uma mensagem.",
  message_body_required: "Existe uma mensagem sem conteúdo.",
  scheduled_at_must_be_future: "Todos os agendamentos devem ser no futuro.",
};

export function ScheduleCloseDialog({
  open,
  onOpenChange,
  conversationId,
  leadName,
  workspaceId,
  onJustClose,
  onJustClosePreserveAssignees,
  onScheduledClose,
}: ScheduleCloseDialogProps) {
  const { toast } = useToast();
  const [mode, setMode] = useState<ScheduleMode>("choose");
  const [messages, setMessages] = useState<DraftScheduleMessage[]>([emptyScheduleMessage()]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setMode("choose");
      setMessages([emptyScheduleMessage()]);
    }
  }, [open]);

  const handleSubmit = async () => {
    const closeConversation = mode === "schedule_close";

    setIsSubmitting(true);
    try {
      const payload = buildSchedulePayload(messages);

      const { error } = await supabase.rpc("schedule_messages_batch", {
        _conversation_id: conversationId,
        _messages: payload as unknown as never,
        _close_conversation: closeConversation,
      });

      if (error) {
        const msg = SCHEDULE_ERROR_MAP[error.message] || error.message;
        throw new Error(msg);
      }

      toast({
        title: closeConversation ? "Conversa encerrada com agendamento" : "Mensagens agendadas",
        description: `${payload.length} mensagem(ns) agendada(s) com sucesso.`,
      });

      onScheduledClose(closeConversation);
      onOpenChange(false);
    } catch (err: unknown) {
      toast({
        title: "Erro ao agendar",
        description: err instanceof Error ? err.message : "Falha ao agendar mensagens.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto overflow-x-hidden">
        <DialogHeader>
          <DialogTitle>Ações da conversa</DialogTitle>
          <DialogDescription>
            {leadName} — escolha como deseja prosseguir.
          </DialogDescription>
        </DialogHeader>

        {mode === "choose" && (
          <div className="grid gap-3 py-2">
            <ScheduleActionOption
              icon={CheckCircle}
              iconClassName="text-muted-foreground"
              title="Apenas encerrar"
              description="Encerra a conversa sem agendar mensagem e devolve o lead ao pool."
              onClick={() => {
                onJustClose();
                onOpenChange(false);
              }}
            />

            {onJustClosePreserveAssignees && (
              <ScheduleActionOption
                icon={CheckCircle}
                title="Encerrar sem desatribuir"
                description="Encerra a conversa e mantém os responsáveis atribuídos."
                onClick={() => {
                  onJustClosePreserveAssignees();
                  onOpenChange(false);
                }}
              />
            )}

            <ScheduleActionOption
              icon={CalendarPlus}
              title="Agendar e encerrar"
              description="Agenda até 5 mensagens e encerra a conversa agora."
              onClick={() => setMode("schedule_close")}
            />

            <ScheduleActionOption
              icon={CalendarClock}
              title="Agendar sem encerrar"
              description="Agenda até 5 mensagens e mantém a conversa aberta."
              onClick={() => setMode("schedule_open")}
            />
          </div>
        )}

        {(mode === "schedule_close" || mode === "schedule_open") && (
          <ScheduleMessagesForm
            workspaceId={workspaceId}
            messages={messages}
            onMessagesChange={setMessages}
            submitLabel={mode === "schedule_close" ? "Agendar e encerrar" : "Agendar"}
            onSubmit={handleSubmit}
            onBack={() => setMode("choose")}
            isSubmitting={isSubmitting}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
